import { CollapsibleSection } from '@strata/react-collapsible-section';
import { DollarInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { ClaimUomMap } from 'services/MedicalBillingConstants';
import { useGetProductCodeMapQuery } from 'store/api/referenceApi';

/** Regex to allow 0-4 comma separated two character codes */
const MODIFIER_CODE_REGEX = /^[\dA-z]{2}(?:,[\dA-z]{2}){0,3}$/;

const ValidationSchemaObject = {
    services: Yup.array(
        Yup.object({
            submittedAmount: Yup.number().required(validationStrings.requiredField.errorMessage),
            diagnoses: Yup.array()
                .of(Yup.string().required())
                .required(validationStrings.requiredField.errorMessage)
                .min(1, validationStrings.requiredField.errorMessage),
            procedureCode: Yup.string().required(validationStrings.requiredField.errorMessage),
            procedureCodeDescription: Yup.string().required(
                validationStrings.requiredField.errorMessage,
            ),
            modifierCodes: Yup.string().matches(
                MODIFIER_CODE_REGEX,
                'Must be up to 4 comma separated two digit codes.',
            ),
            ndc: Yup.string(),
            // Non-editable fields
            dateOfService: Yup.string().required(validationStrings.requiredField.errorMessage),
            dateOfServiceEnd: Yup.string().required(validationStrings.requiredField.errorMessage),
            dispensedQty: Yup.number(),
            dispensedQtyUom: Yup.string().oneOf(Object.keys(ClaimUomMap)),
            billingQty: Yup.number().required(validationStrings.requiredField.errorMessage),
            billingQtyUom: Yup.string()
                .oneOf(Object.keys(ClaimUomMap))
                .required(validationStrings.requiredField.errorMessage),
        }),
    ).required(validationStrings.requiredField.errorMessage),
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ValidationSchema = Yup.object(ValidationSchemaObject);

interface ResubmitClaimServicesProps {
    diagnosisCodeOptions: { name: string; secondaryData: string; code: string }[];
    isDiagnosisCodeDescriptionMapLoading?: boolean;
}

export const ResubmitClaimServices = ({
    diagnosisCodeOptions,
    isDiagnosisCodeDescriptionMapLoading = false,
}: ResubmitClaimServicesProps) => {
    const formik = useFormikContext<Yup.InferType<typeof ValidationSchema>>();

    const { data: productCodeMap, isLoading: isProductCodeMapLoading } =
        useGetProductCodeMapQuery();

    const productCodeArray = useMemo(
        () =>
            Object.entries(productCodeMap ?? {}).map(([key, value]) => ({
                name: value.description,
                secondaryData: key,
                code: key,
                codesystem: value.codesystem,
            })),
        [productCodeMap],
    );

    const productCodeOptions = useMemo(
        () => productCodeArray.filter(({ codesystem }) => codesystem !== 'NDC'),
        [productCodeArray],
    );
    const ndcOptions = useMemo(
        () => productCodeArray.filter(({ codesystem }) => codesystem === 'NDC'),
        [productCodeArray],
    );

    return (
        <FieldArray name="services">
            {(arrayHelpers) =>
                formik.values.services?.map((service, index) => {
                    const title = `Service ${index + 1}`;
                    const idPrefix = `${arrayHelpers.name}.${index}.`;
                    return (
                        <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            className="padding__bottom--medium"
                        >
                            <CollapsibleSection
                                title={title}
                                renderExpanded={false}
                                content={
                                    <>
                                        <DollarInputFormik
                                            id={`${idPrefix}submittedAmount`}
                                            label="Submitted Amount"
                                            required
                                        />
                                        <SuggestMultiSelectFormik
                                            id={`${idPrefix}diagnoses`}
                                            label="Diagnoses"
                                            required
                                            isLoading={isDiagnosisCodeDescriptionMapLoading}
                                            values={diagnosisCodeOptions}
                                            selectedItems={diagnosisCodeOptions.filter(({ code }) =>
                                                (
                                                    getIn(
                                                        formik.values,
                                                        `${idPrefix}diagnoses`,
                                                    ) as string[]
                                                ).includes(code),
                                            )}
                                            onChange={(newValues) => {
                                                (() =>
                                                    formik.setFieldValue(
                                                        `${idPrefix}diagnoses`,
                                                        newValues?.map(({ code }) => code),
                                                    ))();
                                            }}
                                        />
                                        <SuggestSingleSelectFormik
                                            id={`${idPrefix}procedureCode`}
                                            label="Product Code"
                                            required
                                            isLoading={isProductCodeMapLoading}
                                            values={productCodeOptions}
                                            selectedItem={productCodeOptions?.find(
                                                ({ code }) =>
                                                    code ===
                                                    getIn(
                                                        formik.values,
                                                        `${idPrefix}procedureCode`,
                                                    ),
                                            )}
                                            onChange={(newVal) =>
                                                arrayHelpers.replace(index, {
                                                    ...service,
                                                    procedureCode: newVal?.code,
                                                    procedureCodeDescription: newVal?.name,
                                                })
                                            }
                                        />
                                        <TextInputFormik
                                            id={`${idPrefix}modifierCodes`}
                                            label="Modifier Code(s)"
                                            helperText="Enter up to 4 modifier codes separated by commas."
                                        />
                                        <SuggestSingleSelectFormik
                                            id={`${idPrefix}ndc`}
                                            label="NDC"
                                            isLoading={isProductCodeMapLoading}
                                            values={ndcOptions}
                                            selectedItem={ndcOptions?.find(
                                                ({ code }) =>
                                                    code === getIn(formik.values, `${idPrefix}ndc`),
                                            )}
                                            onChange={(newVal) => {
                                                (() =>
                                                    formik.setFieldValue(
                                                        `${idPrefix}ndc`,
                                                        newVal?.code,
                                                    ))();
                                            }}
                                        />
                                    </>
                                }
                            />
                        </div>
                    );
                })
            }
        </FieldArray>
    );
};

ResubmitClaimServices.validationSchema = ValidationSchemaObject;

ResubmitClaimServices.submitValues = (values: Yup.InferType<typeof ValidationSchema>) => ({
    services: values.services.map((service) => ({
        provider_charge: service.submittedAmount,
        diagnoses: service.diagnoses,
        procedure_code: service.procedureCode,
        procedure_code_description: service.procedureCodeDescription,
        modifier_codes: service.modifierCodes?.split(','),
        ndc: service.ndc,
        // Non-editable fields
        date_of_service: service.dateOfService,
        date_of_service_end: service.dateOfServiceEnd,
        dispensed_quantity: service.dispensedQty,
        dispensed_uom: service.dispensedQtyUom,
        billing_quantity: service.billingQty,
        billing_uom: service.billingQtyUom,
    })),
});
